import React, { useMemo } from "react";

import { Box, Typography, Alert } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import { SalesDataResponse } from "../../api/PartnerPortalGatewayApi";
import { normalizeNumber } from "../../utils/formattingFunctions";
import { createCustomLegendRenderer } from "./customLegendRenderer";
import { theme } from "../../theme";

interface DailyAttendanceChartProps {
  data: SalesDataResponse[];
  title?: string;
  height?: number;
}

// Colors for different care types
// Returns colors mapped by care type: ASC gets #6697a0, BSC gets #69698b, VAC gets default teal
const getCareTypeColors = (careType?: string): string => {
  const colorMap: Record<string, string> = {
    ASC: "#6697a0", // After School Care
    BSC: "#69698b", // Before School Care
    VAC: "#5CE5BB", // Vacation Care (default teal)
  };
  return colorMap[careType || ""] || "#5CE5BB";
};

export const DailyAttendanceChart: React.FC<DailyAttendanceChartProps> = ({
  data,
  title = "Daily Attendance Numbers",
  height = 300,
}) => {
  // Memoize chart data for Recharts
  const { chartResult, careTypeAverages } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartResult: [], careTypeAverages: [] }
    }

    // Calculate averages for each care type
    const careTypeAverages = Object.entries(
      data.reduce((acc, item) => {
        const code = item.code?.toLocaleUpperCase() || "UNKNOWN";
        if (!acc[code]) {
          acc[code] = [];
        }
        acc[code].push(item.numBk);
        return acc;
      }, {} as Record<string, number[]>)
    ).map(([code, values]) => ({
      code,
      average: normalizeNumber(
        values.reduce((sum, val) => sum + val, 0) / values.length,
        1
      ),
    }))
    .filter(item => item.code.startsWith("BSC") || item.code.startsWith("ASC"))
    .sort((a, b) => {
      // Sort priority: BSC variants first, then ASC variants
      const isBscA = a.code.startsWith("BSC");
      const isBscB = b.code.startsWith("BSC");

      if (isBscA && !isBscB) return -1;
      if (!isBscA && isBscB) return 1;
      return a.code.localeCompare(b.code);
    });

    // Group data by care type (exclude VAC for daily attendance)
    const careTypeGroups = data
      .filter((item) => item.code && item.code !== "VAC") // Exclude vacation care
      .reduce(
        (acc, item) => {
          const careType = item.code || "Unknown";
          if (!acc[careType]) {
            acc[careType] = {};
          }

          // Map dayOfWeek to consistent day names
          const dayMapping: Record<string, string> = {
            Monday: "Mon",
            Tuesday: "Tue",
            Wednesday: "Wed",
            Thursday: "Thu",
            Friday: "Fri",
            Mon: "Mon",
            Tue: "Tue",
            Wed: "Wed",
            Thu: "Thu",
            Fri: "Fri",
            MON: "Mon", // Handle uppercase from API
            TUE: "Tue",
            WED: "Wed",
            THU: "Thu",
            FRI: "Fri",
            SAT: "Sat",
            SUN: "Sun",
            "1": "Mon", // In case API returns day numbers
            "2": "Tue",
            "3": "Wed",
            "4": "Thu",
            "5": "Fri",
            "6": "Sat",
            "7": "Sun",
          };

          const dayKey = dayMapping[item.dayOfWeek] || item.dayOfWeek || "Unknown";
          if (!acc[careType][dayKey]) {
            acc[careType][dayKey] = [];
          }

          acc[careType][dayKey].push(item.numBk);
          return acc;
        },
        {} as Record<string, Record<string, number[]>>,
      );

    // Convert to chart format: each day is a data point with care types as properties
    // This allows days on x-axis with BSC/ASC bars for each day
    const chartResult = ["Mon", "Tue", "Wed", "Thu", "Fri"].map((day) => {
      const dayData: any = { day };

      // Calculate averages for each care type on this day
      Object.keys(careTypeGroups).forEach((careType) => {
        const dayValues = careTypeGroups[careType][day] || [];
        dayData[careType] =
          dayValues.length > 0
            ? Number((dayValues.reduce((sum, val) => sum + val, 0) / dayValues.length).toFixed(2))
            : 0;
      });

      return dayData;
    });

    return { chartResult, careTypeAverages };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Alert severity="info">No daily attendance data available for this service</Alert>
      </Box>
    );
  }

  if (chartResult.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Alert severity="warning">
          No valid attendance data found. Please check if the service has attendance records or try a different date
          range.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 4,
            height: 20,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#2c3e50",
            fontFamily: "ABC Whyte",
          }}
        >
          {title}
        </Typography>
      </Box>
      
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Box sx={{ flex: "1 1 auto" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#6c757d",
              mb: 3,
              fontFamily: "ABC Whyte",
            }}
          >
            Daily attendance figures showing day-by-day popularity of the program
          </Typography>
        </Box>

        <Box 
          sx={{ 
            flex: "1 1 auto", 
            display: "flex", 
            flexDirection: { xs: "column", sm: "row", md: "row", lg: "column" },
            gap: 2,
            justifyContent: { xs: "center", sm:"center", md: "center", lg: "flex-end" },
          }}
        >
          {careTypeAverages.map((item) => (
            <Typography
              key={item.code}
              variant="body2"
              lineHeight={1.0}
              sx={{
                color: "#6c757d",
                fontFamily: "ABC Whyte",
                textAlign: { xs: "center", sm: "right" },
              }}
            >
              {item.code} total average attendance: {item.average}
            </Typography>
          ))}
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartResult} margin={{ top: 20, right: 0, left: -20, bottom: 80 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#6c757d", fontSize: 16 }} height={80} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={false}
            label={{ value: "Attendance count", angle: -90, fill: "#6c757d", fontSize: 16 }}
          />

          {/* Custom legend at top with custom styling to ensure that the legend displays in the same order as the bars*/}
          <Legend content={createCustomLegendRenderer({ position: "top" })} verticalAlign="top" height={36}/> 
          {/* BSC bars - purple-gray */}
          <Bar dataKey="BSC" fill={getCareTypeColors("BSC")} radius={[32, 32, 0, 0]} name="Before School">
            <LabelList
              dataKey="BSC"
              position="top"
              formatter={(value) => (value ? normalizeNumber(Number(value), 0) : "")}
            />
          </Bar>         
          {/* ASC bars - teal-gray */}
          <Bar dataKey="ASC" fill={getCareTypeColors("ASC")} radius={[32, 32, 0, 0]} name="After School">
            <LabelList
              dataKey="ASC"
              position="top"
              formatter={(value) => (value ? normalizeNumber(Number(value), 0) : "")}
            />
          </Bar>

        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
